import { partnerAuthHeaders } from './partner-token.ts';

/** Encode each path segment for use in /api/sheets/{path}. */
export function encodeRoutingPath(path: string): string {
  return path
    .split('/')
    .map((s) => encodeURIComponent(s))
    .join('/');
}

export class PartnerFetchError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly bodyText?: string,
  ) {
    super(message);
    this.name = 'PartnerFetchError';
  }
}

/** GET same-origin /api/sheets/{path} and parse JSON (auto-creates unknown sheets). */
export async function fetchRoutingJson<T>(path: string): Promise<T> {
  const url = `/api/sheets/${encodeRoutingPath(path)}`;
  let res: Response;
  const auth = partnerAuthHeaders();
  try {
    res = await fetch(url, {
      credentials: 'same-origin',
      ...(Object.keys(auth).length > 0 ? { headers: auth } : {}),
    });
  } catch (e) {
    throw new PartnerFetchError(
      `Network error fetching ${url}: ${e instanceof Error ? e.message : String(e)}`,
      0,
    );
  }

  const text = await res.text();
  let data: unknown;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    throw new PartnerFetchError(`Invalid JSON from ${url} (HTTP ${res.status})`, res.status, text);
  }

  if (!res.ok) {
    const detail =
      data && typeof data === 'object' && 'detail' in data
        ? String((data as { detail: unknown }).detail)
        : text;
    throw new PartnerFetchError(
      `HTTP ${res.status} for ${url}: ${detail || res.statusText}`,
      res.status,
      text,
    );
  }

  return data as T;
}

/** Browser PDF download for the sheet at `path`. */
export function sheetPdfUrl(path: string): string {
  return `/api/sheets/${encodeRoutingPath(path)}/pdf`;
}
